import type { ChipFile } from '@/components/chat/AttachmentChips';
import { statusToPhase } from '@/components/chat/AttachmentChips';
import type { FileUpload } from '@innermate/shared';

/**
 * 사용자가 채팅 composer 에 붙여둔 첨부 항목. 두 종류가 섞일 수 있다:
 *
 * - `pending`: 드래그드랍/탐색기로 방금 추가했지만 아직 서버에 업로드되지 않은 로컬 File.
 *   메시지 전송 시점에 일괄 업로드되며, 그 사이에는 onSend 핸들러가 transient 'uploading'
 *   phase 를 직접 부여한다. 업로드 실패 시 `error` 가 채워지며 phase 는 'failed'
 * - `uploaded`: AttachFilesDialog 에서 라이브러리 모드로 선택한, 또는 이미 한번 업로드를
 *   거친 서버 측 FileUpload 레코드. 칩 phase 는 서버 status 를 매핑
 */
export type ComposerAttachment =
  | {
      kind: 'pending';
      localId: string;
      file: File;
      /** 'pending' = 대기, 'uploading' = 전송 중, 'failed' = 업로드 실패. */
      phase: 'pending' | 'uploading' | 'failed';
      error?: string;
    }
  | {
      kind: 'uploaded';
      file: FileUpload;
    };

/** composer attachment 식별자. pending 은 localId, uploaded 는 서버 id. */
export function attachmentId(a: ComposerAttachment): string {
  return a.kind === 'pending' ? a.localId : a.file.id;
}

const DOCUMENT_EXTS = ['.pdf', '.hwp', '.hwpx', '.doc', '.docx', '.ppt', '.pptx', '.md', '.txt'];
const TABULAR_EXTS = ['.xlsx', '.xls', '.xlsm', '.xlsb', '.csv', '.psv', '.sam'];
const IMAGE_EXTS = ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.bmp'];

/** 로컬 파일명으로 kind 추정 (pending 칩 아이콘 표시용 — 서버 detectKind 가 최종 판정). */
function guessKindFromName(name: string): FileUpload['kind'] {
  const lower = name.toLowerCase();
  if (TABULAR_EXTS.some((e) => lower.endsWith(e))) return 'excel';
  if (DOCUMENT_EXTS.some((e) => lower.endsWith(e))) return 'document';
  if (IMAGE_EXTS.some((e) => lower.endsWith(e))) return 'image';
  return 'other';
}

/**
 * 칩 표시용 ChipFile 로 변환.
 *
 * `uploaded` 항목은 다이얼로그 선택 시점의 동결 스냅샷이라 워커가 분석을 끝내도 갱신되지 않는다.
 * `liveById` (폴링되는 `['files', …]` 캐시에서 파생한 파일 맵) 를 넘기면 해당 파일의 최신
 * status/queueAhead 로 덮어써 칩이 ready/failed 로 자연 전환되고 "대기 중 · 앞에 N건" 도 갱신된다.
 * `startedAt` (서버 createdAt) 으로 진행 중 칩에 경과 타이머를 표시한다.
 */
export function attachmentToChip(
  a: ComposerAttachment,
  liveById?: Map<string, FileUpload>,
): ChipFile {
  if (a.kind === 'pending') {
    return {
      id: a.localId,
      originalName: a.file.name,
      sizeBytes: a.file.size,
      kind: guessKindFromName(a.file.name),
      phase: a.phase,
      error: a.error,
    };
  }
  const f = a.file;
  const live = liveById?.get(f.id) ?? f;
  const startedAt = Date.parse(live.createdAt);
  return {
    id: f.id,
    originalName: f.originalName,
    sizeBytes: f.sizeBytes,
    kind: f.kind,
    phase: statusToPhase(live.status),
    startedAt: Number.isNaN(startedAt) ? undefined : startedAt,
    queueAhead: live.queueAhead ?? null,
  };
}

/** AttachFilesDialog 가 받는 pendingFiles prop 형태로 변환 (uploaded 항목만 의미가 있다). */
export function uploadedFiles(attachments: ComposerAttachment[]): FileUpload[] {
  const out: FileUpload[] = [];
  for (const a of attachments) {
    if (a.kind === 'uploaded') out.push(a.file);
  }
  return out;
}
